import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { sumEqualsMultiArray } from '@app/shared/validators/sum-equals';
import { BehaviorSubject } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import {
  Account,
  AccountLabels,
  Adjustment,
  StandardAccount,
  TransactionCategory,
  TransactionConfiguration,
} from '../../models/statements.models';
import { StatementsFacade } from '../../services/statements.facade';

@Component({
  selector: 'sbnb-adjustment-preview-pane',
  templateUrl: './adjustment-preview-pane.component.html',
  styles: [
    `
      :host ::ng-deep .mat-form-field-prefix {
        top: 0 !important;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdjustmentPreviewPaneComponent implements OnInit {
  @Input() set adjustment(value: Adjustment) {
    this._adjustment = value;
    if (value) {
      this.initForm(value);
      this.statementsFacade
        .getTransactionConfiguration(value.property_id)
        .pipe(take(1))
        .subscribe((configuration) => this.transactionConfiguration$.next(configuration));
    }
  }
  get adjustment(): Adjustment {
    return this._adjustment;
  }
  private _adjustment!: Adjustment;

  @Output() closed = new EventEmitter<void>();
  @Output() applied = new EventEmitter<void>();

  public form: FormGroup;
  public saving$ = new BehaviorSubject<boolean>(false);

  public accountOptions = [...Object.values(StandardAccount), ...Object.values(Account)]
    .filter((account) => account !== Account.HostServiceFee)
    .map((account) => ({
      label: AccountLabels[account],
      value: account,
    }));

  public transactionConfiguration$ = new BehaviorSubject<TransactionConfiguration>(null);

  constructor(
    private fb: FormBuilder,
    private statementsFacade: StatementsFacade,
    private toast: ToastNotificationsService
  ) {
    this.form = this.fb.group({
      accounts: this.fb.array([]),
      transactions: this.fb.array([]),
    });
  }

  ngOnInit(): void {}

  get accounts(): FormArray {
    return this.form.get('accounts') as FormArray;
  }

  get transactions(): FormArray {
    return this.form.get('transactions') as FormArray;
  }

  get totalAmount(): number {
    const accountsTotal = this.accounts.controls.reduce((sum, control) => sum + (control.get('amount')?.value || 0), 0);
    const transactionsTotal = this.transactions.controls.reduce(
      (sum, control) => sum + (control.get('amount')?.value || 0),
      0
    );
    return accountsTotal + transactionsTotal;
  }

  get remainingAmount(): number {
    return this.adjustment ? this.adjustment.amount - this.totalAmount : 0;
  }

  private initForm(adjustment: Adjustment): void {
    this.accounts.clear();
    this.transactions.clear();

    adjustment.account_classification.forEach((financial) => {
      this.accounts.push(
        this.fb.group({
          type: [financial.type ?? StandardAccount.Accommodation, Validators.required],
          amount: [financial.amount, [Validators.required]],
        })
      );
    });

    adjustment.transaction_classification?.forEach((transaction) => {
      this.transactions.push(
        this.fb.group({
          category: [transaction.category, Validators.required],
          amount: [transaction.amount, [Validators.required]],
        })
      );
    });

    // Set validator to ensure amounts sum up to adjustment total
    this.form.setValidators([sumEqualsMultiArray(adjustment.amount, 'amount', ['accounts', 'transactions'])]);
    this.form.updateValueAndValidity();
  }

  removeAccount(index: number): void {
    this.accounts.removeAt(index);
  }

  removeTransaction(index: number): void {
    this.transactions.removeAt(index);
  }

  addAccount(): void {
    this.accounts.push(
      this.fb.group({
        type: [StandardAccount.Accommodation, Validators.required],
        amount: [0, [Validators.required]],
      })
    );
  }

  addTransaction(): void {
    this.transactions.push(
      this.fb.group({
        category: [TransactionCategory.Uncategorized, Validators.required],
        amount: [0, [Validators.required]],
      })
    );
  }

  onClose(): void {
    this.closed.emit();
  }

  onApply(): void {
    if (this.form.invalid || this.saving$.value) {
      return;
    }

    this.saving$.next(true);
    const financials = this.accounts.value;
    const transactions = this.transactions.value;

    this.statementsFacade
      .applyAdjustment(this.adjustment.uuid, financials, transactions)
      .pipe(finalize(() => this.saving$.next(false)))
      .subscribe({
        next: () => {
          this.onClose();
          this.applied.emit();
        },
        error: (error) => {
          this.toast.error(`Oops, an error occurred while applying the adjustment: ${error.message}`);
        },
      });
  }
}
